import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faArrowLeft,
    faUser,
    faEdit,
    faPlus,
    faLock,
    faRotateRight,
    faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import ModalCloseTicket from './ModalCloseTicket';
import ModalKeepClosedTicket from './ModalKeepClosedTicket';
import { getClientNoteColorStyle } from './ClientNotesModal';

const getEstadoInfo = (estado, isDark = false) => {
    switch (estado) {
        case 1:
            return {
                text: 'Abierto',
                style: isDark
                    ? { backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }
                    : { backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }
            };
        case 2:
            return {
                text: 'Cerrado',
                style: isDark
                    ? { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#f87171', border: '1px solid rgba(248, 113, 113, 0.3)' }
                    : { backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }
            };
        case 3:
            return {
                text: 'En progreso',
                style: isDark
                    ? { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }
                    : { backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }
            };
        default:
            return {
                text: `Estado #${estado || 1}`,
                style: isDark
                    ? { backgroundColor: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }
                    : { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
            };
    }
};

const TicketChatHeader = ({
    ticket,
    onBack,
    onTicketUpdated,
    onTicketClosed,
    clientNotes = [],
    onOpenClientNotesModal,
    isOver24Hours = false,
    onOpenTemplateModal,
    onCreateTicketNote
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const isDarkMode = Boolean(
        isDark ||
        (typeof document !== 'undefined' && (
            document.documentElement.getAttribute('data-bs-theme') === 'dark' ||
            document.documentElement.classList.contains('dark') ||
            document.body.classList.contains('dark')
        ))
    );
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showKeepClosedModal, setShowKeepClosedModal] = useState(false);

    const estadoInfo = getEstadoInfo(ticket.iEstadoTicket, isDarkMode);
    const isCerrado = ticket.iEstadoTicket === 2;
    const isInProgress = Number(ticket.iEstadoTicket) === 3;
    const isReabierto = Boolean(ticket.bReabierto || ticket.isReopened);
    const hasAssignedAgent = Boolean(ticket?.idUsuarioAsignado && Number(ticket.idUsuarioAsignado) > 0);

    const contactName = ticket.vNombreContacto?.trim() || ticket.contactName?.trim();
    const username = ticket.vUsername?.trim() || ticket.username?.trim();

    const displayName =
        ticket.vCustomerName?.trim() ||
        ticket.customerName?.trim() ||
        contactName ||
        (username ? `@${username}` : null) ||
        (ticket.vTelefono || ticket.phone || ticket.ticketPhone
            ? `WhatsApp (${ticket.vTelefono || ticket.phone || ticket.ticketPhone})`
            : ticket.vFromUserId
                ? `WhatsApp (${ticket.vFromUserId})`
                : 'Cliente');

    // Botón reutilizable para cerrar ticket (Ultra compacto para móvil)
    const renderCloseButton = (extraClasses = '') => (
        <button
            className={`btn btn-sm px-2 py-1 rounded-2 d-inline-flex align-items-center gap-1 shadow-none ${extraClasses}`}
            style={{
                backgroundColor: isDark
                    ? (isCerrado || !hasAssignedAgent
                        ? 'rgba(255,255,255,0.06)'
                        : isReabierto
                            ? 'rgba(245, 158, 11, 0.2)'
                            : 'rgba(239, 68, 68, 0.2)')
                    : (isCerrado || !hasAssignedAgent
                        ? '#f1f5f9'
                        : isReabierto
                            ? '#fef3c7'
                            : '#fee2e2'),
                color: isDark
                    ? (isCerrado || !hasAssignedAgent
                        ? '#64748b'
                        : isReabierto
                            ? '#fbbf24'
                            : '#f87171')
                    : (isCerrado || !hasAssignedAgent
                        ? '#94a3b8'
                        : isReabierto
                            ? '#b45309'
                            : '#dc2626'),
                border: isDark
                    ? (isCerrado || !hasAssignedAgent
                        ? '1px solid #334155'
                        : isReabierto
                            ? '1px solid rgba(245, 158, 11, 0.4)'
                            : '1px solid rgba(239, 68, 68, 0.4)')
                    : (isCerrado || !hasAssignedAgent
                        ? '1px solid #cbd5e1'
                        : isReabierto
                            ? '1px solid #fde68a'
                            : '1px solid #fecaca'),
                fontSize: '0.62rem',
                fontWeight: 600,
                lineHeight: 1.2,
                cursor: isCerrado || !hasAssignedAgent ? 'not-allowed' : 'pointer',
                opacity: !hasAssignedAgent && !isCerrado ? 0.75 : 1
            }}
            onClick={() => {
                if (isReabierto) {
                    setShowKeepClosedModal(true);
                } else {
                    setShowCloseModal(true);
                }
            }}
            disabled={isCerrado || !hasAssignedAgent}
            title={
                isCerrado
                    ? 'Este ticket ya está cerrado'
                    : !hasAssignedAgent
                        ? 'Debes asignar un agente antes de poder cerrar el ticket'
                        : isReabierto
                            ? 'Mantener cerrado el ticket sin enviar mensaje al cliente'
                            : 'Cerrar ticket'
            }
        >
            <FontAwesomeIcon
                icon={
                    !hasAssignedAgent
                        ? faLock
                        : isReabierto
                            ? faRotateRight
                            : faCheckCircle
                }
                style={{ fontSize: '0.58rem' }}
            />
            <span>
                {isCerrado
                    ? 'Ticket Cerrado'
                    : isReabierto
                        ? 'Mantener Cerrado'
                        : 'Cerrar Ticket'}
            </span>
        </button>
    );

    return (
        <>
            <div
                className="p-2 p-sm-3 border-bottom d-flex justify-content-between align-items-center gap-2"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#e2e8f0',
                    minWidth: 0
                }}
            >
                {/* Customer info & Back */}
                <div className="d-flex align-items-center gap-2 gap-sm-3 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none p-0 flex-shrink-0"
                            style={{
                                width: '32px',
                                height: '32px',
                                minWidth: '32px',
                                borderRadius: '8px',
                                backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc',
                                border: isDarkMode ? '1px solid #233142' : '1px solid #e2e8f0',
                                color: isDarkMode ? '#94a3b8' : '#64748b'
                            }}
                            title="Volver a la lista de tickets"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '0.85rem' }} />
                        </button>
                    )}

                    {/* Avatar con punto verde indicador */}
                    <div className="position-relative flex-shrink-0">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                            style={{
                                width: '38px',
                                height: '38px',
                                backgroundColor: isDarkMode ? '#1e293b' : (ticket.initials ? '#dcfce7' : '#f1f5f9'),
                                color: isDarkMode ? '#34d399' : (ticket.initials ? '#15803d' : '#64748b'),
                                fontSize: '0.90rem',
                                border: isDarkMode ? '1px solid #233142' : 'none'
                            }}
                        >
                            {ticket.initials || <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.80rem' }} />}
                        </div>
                        <span
                            className="position-absolute bottom-0 end-0 rounded-circle border border-white d-sm-none"
                            style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: '#16a34a'
                            }}
                        />
                    </div>

                    <div className="overflow-hidden flex-grow-1 ms-1 ms-sm-0" style={{ minWidth: 0 }}>
                        {/* VISTA DESKTOP (Cliente, Notas, Badges) */}
                        <div className="d-none d-sm-flex align-items-center gap-2 flex-wrap">
                            <h6 className="fw-bold mb-0 text-truncate" style={{ fontSize: '0.92rem', color: isDarkMode ? '#f9fafd' : '#0f172a', maxWidth: '100%' }}>
                                {displayName}
                            </h6>
                            {ticket.vUsername && (
                                <span
                                    className="badge px-2 py-0.5"
                                    style={{
                                        fontSize: '0.70rem',
                                        backgroundColor: isDarkMode ? 'rgba(37, 211, 102, 0.15)' : '#e8f5e9',
                                        color: isDarkMode ? '#4ade80' : '#16a34a',
                                        borderRadius: '6px'
                                    }}
                                >
                                    @{ticket.vUsername}
                                </span>
                            )}

                            {/* Notas del Cliente */}
                            {ticket.idClienteAkasia ? (
                                <>
                                    {clientNotes.map((n) => {
                                        const noteStyle = getClientNoteColorStyle(n.noteColor, isDarkMode);
                                        return (
                                            <button
                                                key={n.idClienteNotas}
                                                type="button"
                                                onClick={() => onOpenClientNotesModal && onOpenClientNotesModal(n)}
                                                className="btn btn-sm py-0 px-2 rounded-pill d-inline-flex align-items-center gap-1 shadow-none"
                                                style={{
                                                    fontSize: '0.74rem',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    backgroundColor: noteStyle.backgroundColor,
                                                    color: noteStyle.color,
                                                    border: `1px solid ${noteStyle.borderColor || (isDarkMode ? '#334155' : '#cbd5e1')}`,
                                                    maxWidth: '220px'
                                                }}
                                                title={`Editar nota: "${n.note}"`}
                                            >
                                                <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>•</span>
                                                <span className="text-truncate" style={{ maxWidth: '170px' }}>
                                                    {n.note}
                                                </span>
                                                <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.65rem', opacity: 0.85 }} />
                                            </button>
                                        );
                                    })}

                                    <button
                                        type="button"
                                        onClick={() => onOpenClientNotesModal && onOpenClientNotesModal(null)}
                                        className="btn btn-sm py-0 px-2 rounded-pill d-inline-flex align-items-center gap-1 shadow-none"
                                        style={{
                                            fontSize: '0.73rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            backgroundColor: isDarkMode ? 'rgba(56, 189, 248, 0.15)' : '#eff6ff',
                                            color: isDarkMode ? '#38bdf8' : '#0284c7',
                                            border: isDarkMode ? '1px dashed rgba(56, 189, 248, 0.4)' : '1px dashed #7dd3fc'
                                        }}
                                        title="Agregar una nueva nota a este cliente"
                                    >
                                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.68rem' }} />
                                        <span>Nueva Nota</span>
                                    </button>
                                </>
                            ) : null}

                            <span
                                className="badge border"
                                style={{
                                    fontSize: '0.7rem',
                                    backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                    borderColor: isDarkMode ? '#334155' : '#e2e8f0'
                                }}
                            >
                                Ticket #{ticket.id}
                            </span>
                            {isReabierto && (
                                <span
                                    className="px-2 py-0.5 rounded fw-bold d-inline-flex align-items-center gap-1"
                                    style={{
                                        fontSize: '0.70rem',
                                        backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fdf2f8',
                                        color: isDarkMode ? '#f472b6' : '#db2777',
                                        border: isDarkMode ? '1px solid rgba(236, 72, 153, 0.35)' : '1px solid #fbcfe8'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.62rem' }} />
                                    Reabierto
                                </span>
                            )}
                            <span
                                className="px-2 py-0.5 rounded fw-bold"
                                style={{ fontSize: '0.70rem', ...estadoInfo.style }}
                            >
                                {estadoInfo.text}
                            </span>
                        </div>

                        {/* VISTA MÓVIL (Ticket #ID + Estado + Botón Cerrar Ultra Compacto) */}
                        <div className="d-flex d-sm-none align-items-center gap-1.5 flex-nowrap mb-1">
                            <span
                                className="fw-bold me-2 text-nowrap"
                                style={{
                                    fontSize: '0.80rem',
                                    color: isDarkMode ? '#f9fafd' : '#0f172a'
                                }}
                            >
                                Ticket #{ticket.id}
                            </span>

                            <span
                                className="px-1 py-1 rounded me-2 fw-bold text-nowrap"
                                style={{ fontSize: '0.65rem', ...estadoInfo.style }}
                            >
                                {estadoInfo.text}
                            </span>

                            {isInProgress && isOver24Hours && (
                                <button
                                    type="button"
                                    onClick={onOpenTemplateModal}
                                    className="btn btn-sm px-2 py-1 rounded-2 d-inline-flex align-items-center gap-1 shadow-none text-white text-nowrap ms-1"
                                    style={{
                                        backgroundColor: '#4f46e5',
                                        fontSize: '0.62rem',
                                        fontWeight: 600,
                                        border: 'none'
                                    }}
                                    title="Enviar plantilla de Facebook (>24h)"
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '0.58rem' }} />
                                    <span>Plantilla Facebook</span>
                                </button>
                            )}

                            {/* Botón de cerrar ticket muy compacto pegado al status */}
                            {renderCloseButton('text-nowrap ms-1')}
                        </div>

                        {/* Subtexto "En línea (WhatsApp)" */}
                        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.74rem', color: '#16a34a' }}>
                            <span
                                className="rounded-circle d-none d-sm-inline-block"
                                style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }}
                            />
                            <span>En línea (WhatsApp)</span>
                        </div>
                    </div>
                </div>

                {/* Action button: Cerrar Ticket visible únicamente en desktop */}
                <div className="d-none d-sm-flex align-items-center flex-shrink-0">
                    {isInProgress && isOver24Hours && (
                        <button
                            type="button"
                            onClick={onOpenTemplateModal}
                            className="btn btn-sm px-2.5 py-1 rounded-2 d-inline-flex align-items-center gap-1.5 shadow-none text-white me-2"
                            style={{
                                backgroundColor: '#4f46e5',
                                fontSize: '0.74rem',
                                fontWeight: 600,
                                border: 'none'
                            }}
                            title="Enviar plantilla de Facebook para reanudar la conversación (>24h)"
                        >
                            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '0.72rem' }} />
                            <span>Enviar Plantilla Facebook</span>
                        </button>
                    )}
                    {renderCloseButton()}
                </div>
            </div>

            {/* Modal separado para confirmar cierre de ticket */}
            <ModalCloseTicket
                show={showCloseModal}
                onClose={() => setShowCloseModal(false)}
                ticket={ticket}
                onTicketClosed={onTicketClosed || onTicketUpdated}
                onCreateTicketNote={onCreateTicketNote}
            />

            {/* Modal de confirmación para mantener cerrado sin enviar mensaje */}
            <ModalKeepClosedTicket
                show={showKeepClosedModal}
                onClose={() => setShowKeepClosedModal(false)}
                ticket={ticket}
                onTicketClosed={onTicketClosed || onTicketUpdated}
                onCreateTicketNote={onCreateTicketNote}
            />
        </>
    );
};

TicketChatHeader.propTypes = {
    ticket: PropTypes.object.isRequired,
    onBack: PropTypes.func,
    onTicketUpdated: PropTypes.func,
    onTicketClosed: PropTypes.func,
    onOpenAssignModal: PropTypes.func,
    clientNotes: PropTypes.array,
    onOpenClientNotesModal: PropTypes.func,
    isOver24Hours: PropTypes.bool,
    onOpenTemplateModal: PropTypes.func,
    onCreateTicketNote: PropTypes.func
};

export default TicketChatHeader;